import styled, { keyframes } from "styled-components";
import { BRAND_C as C, COLORS } from "../../common/colors";

// ─── Keyframes ────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0);    }
`;
export const kScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1);    }
`;
export const kShimmer = keyframes`
  0%   { background-position: -500px 0; }
  100% { background-position:  500px 0; }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE SHELL
// ═══════════════════════════════════════════════════════════════════════════════

export const Page = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${COLORS.pageBg};
  overflow-x: hidden;
`;
Page.displayName = "Page";

export const BgBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;

  &.b1 { width:520px; height:520px; background:rgba(108,71,255,0.08); top:-160px; left:-160px; }
  &.b2 { width:380px; height:380px; background:rgba(245,197,24,0.07); top:35%; right:-120px; }
  &.b3 { width:280px; height:280px; background:rgba(108,71,255,0.05); bottom:120px; left:32%; }

  @media (max-width: 48em) {
    &.b1 { width:260px; height:260px; }
    &.b2 { width:200px; height:200px; }
    &.b3 { display: none; }
  }
`;
BgBlob.displayName = "BgBlob";

export const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image: radial-gradient(circle, rgba(108,71,255,0.06) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(to bottom, transparent, rgba(0,0,0,0.4) 15%, rgba(0,0,0,0.4) 85%, transparent);
`;
DotGrid.displayName = "DotGrid";

export const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px;

  @media (min-width: 48em) { padding: 0 32px; }
  @media (min-width: 64em) { padding: 0 48px; }
`;
Inner.displayName = "Inner";

// ═══════════════════════════════════════════════════════════════════════════════
// HERO HEADER — matches ContactUs pattern
// ═══════════════════════════════════════════════════════════════════════════════

export const HeroWrap = styled.header`
  position: relative;
  z-index: 1;
  text-align: center;
  padding-top: 38px;
  padding-right: 24px;
  padding-bottom: 52px;
  padding-left: 24px;
  animation: ${kFadeUp} 0.65s ease both;

  @media (max-width: 600px) { padding: 56px 20px 36px; }
`;
HeroWrap.displayName = "HeroWrap";

export const HeroEyebrow = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${C.brand};
  margin-bottom: 12px;
`;
HeroEyebrow.displayName = "HeroEyebrow";

export const HeroTitle = styled.h1`
  font-size: clamp(2rem, 5.5vw, 3.2rem);
  font-weight: 900;
  color: ${C.text};
  letter-spacing: -0.025em;
  line-height: 1.12;
  margin-bottom: 16px;
`;
HeroTitle.displayName = "HeroTitle";

export const HeroDivider = styled.div`
  width: 52px; height: 3px;
  background: linear-gradient(90deg, ${C.brand}, ${C.accent});
  border-radius: 2px;
  margin: 0 auto 18px;
`;
HeroDivider.displayName = "HeroDivider";

export const HeroDesc = styled.p`
  font-size: clamp(0.9rem, 2vw, 1rem);
  color: ${C.textMid};
  line-height: 1.78;
  max-width: 520px;
  margin: 0 auto;
`;
HeroDesc.displayName = "HeroDesc";

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY FILTER BAR
// ═══════════════════════════════════════════════════════════════════════════════

export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 40px;
  padding: 0 4px;
  animation: ${kFadeUp} 0.5s ease both 0.1s;
`;
FilterBar.displayName = "FilterBar";

export const FilterBtn = styled.button`
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: ${({ $active }) => $active ? 700 : 500};
  cursor: pointer;
  min-height: 38px;
  white-space: nowrap;
  transition: background 0.2s, border-color 0.2s, color 0.2s,
              box-shadow 0.2s, transform 0.15s;
  border: 1.5px solid ${({ $active }) => $active ? C.brand : C.border};
  background: ${({ $active }) => $active
    ? `linear-gradient(135deg, ${C.brandDark}, ${C.brand})`
    : C.surface};
  color: ${({ $active }) => $active ? "#fff" : C.textMid};
  box-shadow: ${({ $active }) => $active
    ? `0 3px 14px rgba(108,71,255,0.32)`
    : "0 1px 4px rgba(0,0,0,0.05)"};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: ${C.brand};
    box-shadow: 0 5px 18px rgba(108,71,255,0.24);
  }
  &:active { transform: translateY(0) scale(0.97); }
  &:focus-visible { outline: 2px solid ${C.brand}; outline-offset: 2px; }
`;
FilterBtn.displayName = "FilterBtn";

// ═══════════════════════════════════════════════════════════════════════════════
// BLOG CARDS GRID
// ═══════════════════════════════════════════════════════════════════════════════

export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 560px)  { grid-template-columns: repeat(2, 1fr); }
  @media (min-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
`;
CardsGrid.displayName = "CardsGrid";

// ─── Featured hero card (first post) ─────────────────────────────────────────

export const HeroCard = styled.article`

  /* remove underline on hover/focus (overrides global a:hover) */
  &, &:hover, &:focus, &:active, &:visited,
  &:hover *, &:focus *, & * {
    text-decoration: none !important;
  }
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border-radius: 20px;
  overflow: hidden;
  background: ${C.surface};
  border: 1px solid ${C.border};
  box-shadow: 0 4px 28px rgba(108,71,255,0.10);
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 48px rgba(108,71,255,0.18);
  }
  &:focus-visible { outline: 2px solid ${C.brand}; outline-offset: 3px; }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.5s ease both;
  }
`;
HeroCard.displayName = "HeroCard";

export const HeroCardImg = styled.div`
  position: relative;
  min-height: 280px;
  overflow: hidden;
  background: ${COLORS.primaryDark};

  img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
    transition: transform 0.4s ease;
  }
  ${HeroCard}:hover img { transform: scale(1.04); }

  @media (max-width: 700px) { min-height: 200px; }
`;
HeroCardImg.displayName = "HeroCardImg";

export const HeroCardBody = styled.div`
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 700px) { padding: 22px 20px 26px; }
`;
HeroCardBody.displayName = "HeroCardBody";

// ─── Regular card ─────────────────────────────────────────────────────────────

export const Card = styled.article`

  /* remove underline on hover/focus (overrides global a:hover) */
  &, &:hover, &:focus, &:active, &:visited,
  &:hover *, &:focus *, & * {
    text-decoration: none !important;
  }
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  background: ${C.surface};
  border: 1px solid ${C.border};
  box-shadow: 0 2px 12px rgba(108,71,255,0.07);
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 40px rgba(108,71,255,0.16);
    border-color: ${COLORS.borderGreen20};
  }
  &:focus-visible { outline: 2px solid ${C.brand}; outline-offset: 3px; }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${kFadeUp} 0.45s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 70, 420)}ms;
  }
`;
Card.displayName = "Card";

export const CardImg = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: ${COLORS.primaryDark};
  position: relative;

  img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
    transition: transform 0.35s ease;
  }
  ${Card}:hover img { transform: scale(1.06); }
`;
CardImg.displayName = "CardImg";

export const CardBody = styled.div`
  padding: 18px 18px 22px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;
CardBody.displayName = "CardBody";

// ─── Shared card content atoms ────────────────────────────────────────────────

export const CategoryPill = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${C.brand};
  background: ${C.muted};
  border: 1px solid ${C.border};
  border-radius: 999px;
  padding: 3px 10px;
  margin-bottom: 10px;
  width: fit-content;
`;
CategoryPill.displayName = "CategoryPill";

export const CardTitle = styled.h3`
  font-size: ${({ $hero }) => $hero ? "clamp(1.3rem, 3vw, 1.75rem)" : "1rem"};
  font-weight: 800;
  color: ${C.text};
  line-height: 1.28;
  margin-bottom: 10px;
  letter-spacing: -0.015em;
  display: -webkit-box;
  -webkit-line-clamp: ${({ $hero }) => $hero ? 3 : 2};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
CardTitle.displayName = "CardTitle";

export const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.72rem;
  color: ${C.textSoft};
  margin-bottom: 10px;
  font-weight: 500;

  span.dot { opacity: 0.4; }
`;
CardMeta.displayName = "CardMeta";

export const CardExcerpt = styled.p`
  font-size: 0.85rem;
  color: ${C.textMid};
  line-height: 1.68;
  margin-bottom: 14px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: ${({ $lines }) => $lines || 3};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
CardExcerpt.displayName = "CardExcerpt";

export const ReadMoreLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${C.brand};
  margin-top: auto;
  transition: gap 0.2s;

  svg { width: 13px; height: 13px; transition: transform 0.2s; }

  ${Card}:hover &,
  ${HeroCard}:hover & { gap: 8px; svg { transform: translateX(2px); } }
`;
ReadMoreLink.displayName = "ReadMoreLink";

// ─── Video badge overlay ──────────────────────────────────────────────────────

export const VideoBadge = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10,4,32,0.35);

  span {
    width: 44px; height: 44px;
    border-radius: 50%;
    background: rgba(255,255,255,0.92);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 16px rgba(0,0,0,0.28);
    transition: transform 0.2s, background 0.2s;

    svg { width: 16px; height: 16px; fill: ${C.brand}; margin-left: 2px; }
  }

  ${Card}:hover span,
  ${HeroCard}:hover span {
    transform: scale(1.12);
    background: ${C.brand};
    svg { fill: white; }
  }
`;
VideoBadge.displayName = "VideoBadge";

// ─── Tags strip ───────────────────────────────────────────────────────────────

export const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 8px;
`;
TagsRow.displayName = "TagsRow";

export const Tag = styled.span`
  font-size: 0.6rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${C.surfaceAlt};
  color: ${C.textSoft};
  border: 1px solid ${C.border};
`;
Tag.displayName = "Tag";

// ─── No-image fallback art (cream gradient + lotus) ───────────────────────────

export const NoImageArt = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 28% 14%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 48%),
    linear-gradient(165deg, #f1f4ec 0%, #f7efe0 100%);

  svg {
    width: 62%;
    max-width: 280px;
    height: auto;
    transition: transform 0.4s ease;
  }
  ${Card}:hover & svg,
  ${HeroCard}:hover & svg { transform: scale(1.06); }
`;
NoImageArt.displayName = "NoImageArt";

// ═══════════════════════════════════════════════════════════════════════════════
// BLOG DESCRIPTION PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export const ArticlePage = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${COLORS.pageBg};
  overflow-x: hidden;
  padding-bottom: 80px;
`;
ArticlePage.displayName = "ArticlePage";

// ─── Article hero ─────────────────────────────────────────────────────────────

export const ArticleHero = styled.div`
  position: relative;
  width: 100%;
  max-height: 480px;
  overflow: hidden;
  background: ${COLORS.primaryDark};

  img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
    max-height: 480px;
  }

  /* gradient bottom fade */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, transparent 40%, rgba(10,4,32,0.72) 100%);
    pointer-events: none;
  }
`;
ArticleHero.displayName = "ArticleHero";

export const ArticleHeroText = styled.div`
  position: absolute;
  bottom: 0; left: 0; right: 0;
  z-index: 1;
  padding: 28px 24px 32px;
  max-width: 900px;
  margin: 0 auto;

  @media (min-width: 48em) { padding: 36px 48px 40px; }
`;
ArticleHeroText.displayName = "ArticleHeroText";

// ─── Article inner layout ─────────────────────────────────────────────────────

export const ArticleInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 48px 20px 64px;

  @media (min-width: 48em) { padding: 56px 32px 80px; }
`;
ArticleInner.displayName = "ArticleInner";

export const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${C.border};
`;
ArticleMeta.displayName = "ArticleMeta";

export const AuthorAvatar = styled.div`
  width: 40px; height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${C.brand}, ${C.brandLight});
  display: flex; align-items: center; justify-content: center;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 800;
  flex-shrink: 0;
`;
AuthorAvatar.displayName = "AuthorAvatar";

export const MetaText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;

  .author { font-size: 0.85rem; font-weight: 700; color: ${C.text}; }
  .date   { font-size: 0.72rem; color: ${C.textSoft}; }
`;
MetaText.displayName = "MetaText";

export const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${C.brand};
  background: ${C.muted};
  border: 1px solid ${C.border};
  border-radius: 999px;
  padding: 7px 16px;
  cursor: pointer;
  margin-left: auto;
  min-height: 36px;
  transition: background 0.2s, transform 0.15s;

  &:hover { background: ${C.border}; transform: translateX(-2px); }
  &:focus-visible { outline: 2px solid ${C.brand}; outline-offset: 2px; }

  svg { width: 13px; height: 13px; }
`;
BackBtn.displayName = "BackBtn";

// ─── Rich content blocks ──────────────────────────────────────────────────────

export const ContentHeading = styled.h2`
  font-size: ${({ $level }) => {
    if ($level === 1) return "clamp(1.5rem, 4vw, 2rem)";
    if ($level === 2) return "clamp(1.2rem, 3vw, 1.55rem)";
    return "clamp(1rem, 2.5vw, 1.25rem)";
  }};
  font-weight: 800;
  color: ${C.text};
  letter-spacing: -0.02em;
  line-height: 1.25;
  margin: 2.2em 0 0.7em;
  &:first-child { margin-top: 0; }
`;
ContentHeading.displayName = "ContentHeading";

export const ContentParagraph = styled.p`
  font-size: clamp(0.92rem, 2vw, 1.02rem);
  color: ${C.textMid};
  line-height: 1.85;
  margin: 0 0 1.2em;
`;
ContentParagraph.displayName = "ContentParagraph";

export const ContentImage = styled.figure`
  margin: 2em 0;

  img {
    width: 100%;
    max-width: 680px;
    height: auto;
    display: block;
    margin: 0 auto;
    border-radius: 14px;
    box-shadow: 0 4px 24px rgba(108,71,255,0.12);
  }
  figcaption {
    text-align: center;
    margin-top: 10px;
    font-size: 0.78rem;
    color: ${C.textSoft};
    font-style: italic;
  }
`;
ContentImage.displayName = "ContentImage";

export const VideoWrap = styled.div`
  margin: 2em 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 28px rgba(108,71,255,0.14);
  background: ${COLORS.primaryDark};
  aspect-ratio: 16 / 9;
  position: relative;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  figcaption {
    position: absolute;
    bottom: 0; left: 0; right: 0;
    padding: 8px 14px;
    font-size: 0.75rem;
    color: rgba(255,255,255,0.75);
    background: rgba(0,0,0,0.4);
    text-align: center;
  }
`;
VideoWrap.displayName = "VideoWrap";

export const ContentList = styled.ul`
  margin: 0 0 1.2em 0;
  padding-left: 1.4em;
  list-style: ${({ $ordered }) => $ordered ? "decimal" : "none"};

  li {
    font-size: clamp(0.9rem, 2vw, 1rem);
    color: ${C.textMid};
    line-height: 1.78;
    margin-bottom: 0.6em;
    position: relative;
    padding-left: ${({ $ordered }) => $ordered ? "0" : "1.2em"};

    /* custom bullet */
    &::before {
      content: ${({ $ordered }) => $ordered ? '""' : '""'};
      display: ${({ $ordered }) => $ordered ? "none" : "block"};
      position: absolute;
      left: 0; top: 0.55em;
      width: 7px; height: 7px;
      border-radius: 50%;
      background: linear-gradient(135deg, ${C.brand}, ${C.brandLight});
      flex-shrink: 0;
    }
  }
`;
ContentList.displayName = "ContentList";

export const TableBlock = styled.div`
  margin: 1.5em 0;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.88rem;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 8px rgba(108,71,255,0.08);
  }
  th {
    background: linear-gradient(135deg, ${C.brandDark}, ${C.brand});
    color: #fff;
    padding: 10px 14px;
    text-align: left;
    font-weight: 700;
    font-size: 0.78rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  td {
    padding: 10px 14px;
    color: ${C.textMid};
    border-bottom: 1px solid ${C.border};
  }
  tr:last-child td { border-bottom: none; }
  tr:nth-child(even) td { background: ${C.surfaceAlt}; }
`;
TableBlock.displayName = "TableBlock";

export const Blockquote = styled.blockquote`
  margin: 2em 0;
  padding: 20px 22px 20px 24px;
  border-left: 4px solid ${C.brand};
  background: ${C.muted};
  border-radius: 0 12px 12px 0;
  position: relative;

  /* decorative quote glyph */
  &::before {
    content: '"';
    position: absolute;
    top: 6px; left: 14px;
    font-size: 3.5rem;
    color: ${C.brand};
    opacity: 0.15;
    font-family: Georgia, serif;
    line-height: 1;
    pointer-events: none;
  }

  p {
    font-size: clamp(0.95rem, 2.2vw, 1.08rem);
    font-style: italic;
    color: ${C.text};
    line-height: 1.72;
    margin: 0 0 8px;
    position: relative;
    z-index: 1;
  }
  footer {
    font-size: 0.78rem;
    font-weight: 700;
    color: ${C.brand};
    font-style: normal;
  }
`;
Blockquote.displayName = "Blockquote";

export const ContentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.88rem;
  font-weight: 700;
  color: ${C.brand};
  text-decoration: none;
  border-bottom: 1.5px solid ${C.border};
  transition: border-color 0.2s, color 0.2s;
  padding-bottom: 1px;
  margin: 0.5em 0;

  svg { width: 12px; height: 12px; flex-shrink: 0; }

  &:hover {
    color: ${C.brandDark};
    border-color: ${C.brand};
  }
`;
ContentLink.displayName = "ContentLink";

// ─── SEO tags section ─────────────────────────────────────────────────────────

export const SeoSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${C.border};
`;
SeoSection.displayName = "SeoSection";

export const SeoLabel = styled.p`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${C.textSoft};
  margin-bottom: 10px;
`;
SeoLabel.displayName = "SeoLabel";

export const SeoTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;
SeoTags.displayName = "SeoTags";

export const SeoTag = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${C.muted};
  color: ${C.brand};
  border: 1px solid ${C.border};
`;
SeoTag.displayName = "SeoTag";

// ─── Empty state ──────────────────────────────────────────────────────────────

export const EmptyState = styled.div`
  text-align: center;
  padding: 80px 24px;
  color: ${C.textSoft};

  .icon { font-size: 3rem; display: block; margin-bottom: 16px; }
  h3 { font-size: 1.1rem; font-weight: 700; color: ${C.text}; margin-bottom: 8px; }
  p  { font-size: 0.88rem; line-height: 1.6; }
`;
EmptyState.displayName = "EmptyState";

// ─── Article hero WITHOUT an image (cream banner + lotus panel) ───────────────

export const ArticleHeroPlain = styled.section`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: 24px;
  width: calc(100% - 40px);
  max-width: 1180px;
  margin: 24px auto 0;
  padding: 36px 44px;
  border-radius: 28px;
  overflow: hidden;
  background: linear-gradient(100deg, #f9f5ee 0%, #f9f5ee 55%, #e4e8de 100%);
  animation: ${kFadeUp} 0.55s ease both;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    padding: 24px 20px;
    border-radius: 22px;
  }
`;
ArticleHeroPlain.displayName = "ArticleHeroPlain";

export const ArticleHeroPlainText = styled.div`
  h1 {
    margin: 0 0 12px;
    font-size: clamp(1.6rem, 4.5vw, 2.8rem);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.03em;
    color: #2c5a41;
  }
  .divider {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    span { width: 26px; height: 2px; background: #8fb09a; }
  }
  .focus {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
    color: #7a6a58;
    margin: 0;
  }
  .focus i {
    width: 9px; height: 12px;
    background: #c4a44a;
    border-radius: 50% 50% 50% 50% / 65% 65% 35% 35%;
    flex-shrink: 0;
  }
`;
ArticleHeroPlainText.displayName = "ArticleHeroPlainText";

export const ArticleHeroPlainArt = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 20px;
  overflow: hidden;

  @media (max-width: 760px) { aspect-ratio: 16 / 9; }
`;
ArticleHeroPlainArt.displayName = "ArticleHeroPlainArt";